/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState, useRef } from 'react';
import {
    object, func, string, bool,
} from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
    Grid, Card, Typography, Button,
} from '@material-ui/core';
import ReactHtmlParser from 'react-html-parser';
import ResponsiveImage from '../../../../../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';

// Number of ms until error is dismissed
const errorTimeoutTime = 2000;
// Number of ms that error Transition lasts
const transitionTime = 500;

const useStyles = makeStyles(({ palette, spacing, breakpoints }) => ({
    itemContainer: {
        boxShadow: `1px 1px 3px -1px ${palette.black}`,
        position: 'relative',
        width: '395px',
        maxWidth: '395px',
        height: '105px',
        display: 'block',
        margin: '12px',
        [breakpoints.down('lg')]: {
            margin: '12px',
        },
        [breakpoints.down('md')]: {
            maxWidth: '355px',
        },
        [breakpoints.down('xs')]: {
            margin: '12px 0px',
            width: '98%',
        },
    },
    itemInnerContainer: {
        display: 'flex',
        flexFlow: 'row nowrap',
    },
    errorContainer: (error) => ({
        position: 'absolute',
        background: palette?.background?.paper || '#fff',
        color: palette?.error.light || 'rgb(220, 30, 30)',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        display: 'flex',
        placeItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
        opacity: error ? 1 : 0,
        transition: `opacity ${transitionTime / 1000}s`,
    }),
    errorContainerHidden: {
        zIndex: '0 !important',
    },
    imageContainer: {
        position: 'relative',
        padding: '0',
        height: '105px',
        width: '105px',
        maxWidth: '105px',
    },
    imageOverlay: {
        position: 'absolute',
        top: '0',
        left: '0',
        height: '100%',
        width: '100%',
        backgroundColor: palette?.bgTextOverlay1 || 'rgba(51, 51, 51, 0.8)',
        color: palette?.getContrastText(palette?.bgTextOverlay1) || '#fff',
        letterSpacing: '0.5px',
        fontWeight: 'bold',
        fontSize: '16px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    mainContent: {
        paddingLeft: spacing(2),
    },
    actionButton: {
        borderRadius: '20px',
        margin: 'auto',
        display: 'flex',
        width: '85%',
        maxWidth: '100px',
    },
    itemImage: {
        height: '105px',
        width: '105px',
        marginBottom: '-6px',
    },
    itemName: {
        paddingRight: spacing(2),
        fontSize: '16px',
        lineHeight: '20px',
    },
}));

const CYOBandItem = ({
    itemData,
    error,
    clearError,
    className,
    handleSelectBand,
    handleRemoveBand,
    added,
}) => {
    // Need a state variable to fade out without abrupt zIndex change
    const [hideError, setHideError] = useState(true);
    const classes = useStyles(!!error);
    const errorTimeout = useRef();
    const transitionTimeout = useRef();

    // Used to set hideError to try at the appropriate time after the error is dismissed
    useEffect(() => {
        if (error) {
            setHideError(false);
            if (errorTimeout.current) {
                clearTimeout(errorTimeout.current);
                errorTimeout.current = null;
            }
            errorTimeout.current = setTimeout(() => clearError(itemData.legacyId), errorTimeoutTime);
        } else {
            if (transitionTimeout.current) {
                clearTimeout(transitionTimeout);
                transitionTimeout.current = null;
            }
            transitionTimeout.current = setTimeout(() => {
                setHideError(true);
            }, transitionTime);
        }
    }, [error]);

    const addRemoveItem = () => {
        if (added) {
            handleRemoveBand();
        } else {
            handleSelectBand(itemData.legacyId, itemData.id);
        }
    };

    // regex will replace every alphabetic or numberic character before ":" with empty string ''
    // thus replacing http or https from image url
    const itemImagePath = itemData.image?.path?.replace(/(^\w+:|^)/, '');
    const itemImageName = itemData.image?.name;
    const imagePath = `${itemImagePath}${itemImageName}x.jpg`;
    return (
        <Grid component={Card} className={`${className} ${classes.itemContainer}`} item sm={12} md={6} lg={4}>
            <Typography className={`${classes.errorContainer} ${hideError ? classes.errorContainerHidden : ''}`}>
                {error}
            </Typography>
            <div className={classes.itemInnerContainer}>
                <div className={classes.imageContainer}>
                    {added ? (
                        <Typography className={classes.imageOverlay}>
                            SELECTED
                        </Typography>
                    ) : null}
                    <ResponsiveImage className={classes.itemImage} path={imagePath} alt={itemData.name} params={{ desktop: 'quality=100', tablet: 'quality=100',  mobile: 'quality=100' }} />
                </div>
                <Grid className={classes.mainContent} alignItems="center" justify="center" container item xs={8}>
                    <Grid item xs={12}>
                        <Typography className={classes.itemName} variant="body2">
                            {ReactHtmlParser(itemData.name)}
                        </Typography>
                    </Grid>

                    <Grid item xs={6} />
                    <Grid justify="center" item xs={6}>
                        <Button
                            data-testid="cyoBandButton"
                            variant="outlined"
                            className={classes.actionButton}
                            onClick={addRemoveItem}
                        >
                            {added ? 'Change' : 'Add'}
                        </Button>
                    </Grid>
                </Grid>
            </div>
        </Grid>
    );
};

CYOBandItem.propTypes = {
    itemData: object.isRequired,
    handleSelectBand: func.isRequired,
    handleRemoveBand: func.isRequired,
    added: bool.isRequired,
    className: string,
    error: string,
    clearError: func,
};

CYOBandItem.defaultProps = {
    error: null,
    clearError: () => {},
    className: '',
};

export default CYOBandItem;
